import { Ref } from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';

/**
 * A styled select component for single or multiple selection.
 */
export function Select({
  className,
  multiple,
  ref,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string; 
  /** React ref for the underlying select element. */
  ref: Ref<HTMLSelectElement> 
} & Omit<
  Headless.SelectProps,
  'as' | 'className'
>) {
  return (
    <span
      className={clsx([
        className,
        // Basic layout
        'group relative block w-full',
        // Background color + shadow applied to inset pseudo element, so shadow
        // blends with border in light mode
        'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm',
        // Background color is moved to control and shadow is removed in
        // dark mode so hide `before` pseudo
        'dark:before:hidden',
        // Focus ring
        'has-data-focus:after:ring-primary-500 has-data-focus:after:ring-2 after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent',
        // Disabled state
        'has-data-disabled:opacity-50 has-data-disabled:before:bg-gray-950/5 has-data-disabled:before:shadow-none',
      ])}
      data-slot="control"
    >
      <Headless.Select
        ref={ref}
        multiple={multiple}
        {...props}
        className={clsx([
          // Basic layout
          'relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]',
          // Horizontal padding
          multiple
            ? 'px-[calc(--spacing(3.5)-1px)] sm:px-[calc(--spacing(3)-1px)]'
            : 'pl-[calc(--spacing(3.5)-1px)] pr-[calc(--spacing(10)-1px)] sm:pl-[calc(--spacing(3)-1px)] sm:pr-[calc(--spacing(9)-1px)]',
          // Options (multi-select)
          '[&_optgroup]:font-semibold',
          // Typography
          'dark:*:text-white text-base/6 text-gray-950 placeholder:text-gray-500 dark:text-white sm:text-sm/6',
          // Border
          'data-hover:border-gray-950/20 dark:data-hover:border-white/20 border border-gray-950/10 dark:border-white/10',
          // Background color
          'dark:*:bg-gray-800 bg-transparent dark:bg-white/5',
          // Hide default focus styles
          'focus:outline-hidden',
          // Invalid state
          'data-invalid:border-red-500 data-invalid:data-hover:border-red-500 dark:data-invalid:border-red-600 dark:data-invalid:data-hover:border-red-600',
          // Disabled state
          'data-disabled:border-gray-950/20 data-disabled:opacity-100 dark:data-disabled:border-white/15 dark:data-disabled:bg-white/[2.5%] dark:data-hover:data-disabled:border-white/15',
        ])}
      />
      {!multiple && (
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg
            aria-hidden="true"
            className="size-5 group-has-data-disabled:stroke-gray-600 sm:size-4 forced-colors:stroke-[CanvasText] stroke-gray-500 dark:stroke-gray-400"
            fill="none"
            viewBox="0 0 16 16"
          >
            <path
              d="M5.75 10.75L8 13L10.25 10.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
            />
            <path
              d="M10.25 5.25L8 3L5.75 5.25"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
            />
          </svg>
        </span>
      )}
    </span>
  );
}
